import type { CatalogRide, RideId } from '@runkr/config/catalog'
import { useState } from 'preact/hooks'
import type { RunnerSlot } from '../runnerPreview/roster'
import { runnerName } from '../runnerPreview/roster'
import { assetUrl } from '../runnerPreview/previewLayout'
import { RideGrid } from '../wardrobe/RideGrid'
import styles from './panels.module.css'
import { Icon } from './icons'
import type { IconName } from './icons'

type Tab = 'runners' | 'rides'
type CostumeSlot = 'hat' | 'face' | 'top' | 'bottoms' | 'shoes' | 'back'

const SLOTS: readonly { id: CostumeSlot; label: string }[] = [
  { id: 'hat', label: 'Hats' },
  { id: 'face', label: 'Faces' },
  { id: 'top', label: 'Tops' },
  { id: 'bottoms', label: 'Bottoms' },
  { id: 'shoes', label: 'Shoes' },
  { id: 'back', label: 'Backs' },
]

/** Six darkened cells under a "Coming soon" label: costume slots (T3.8). */
function ComingSoonGrid({ what }: { what: string }) {
  return (
    <div class={styles.soonGrid}>
      <div class={styles.soonCells} aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} class={styles.soonCell} />
        ))}
      </div>
      <div class={styles.soonOverlay}>
        <strong>Coming soon</strong>
        <span>{what}</span>
      </div>
    </div>
  )
}

function RunnerCard({
  slot,
  equipped,
  onPick,
}: {
  slot: RunnerSlot
  equipped: boolean
  onPick: (charId: number) => void
}) {
  const head = slot.rig?.faces.calm ?? slot.image
  if (!slot.ready) {
    return (
      <div
        class={`${styles.runnerCard} ${styles.runnerSoon}`}
        role="img"
        aria-label="Locked runner: new runner soon"
      >
        <span class={styles.runnerBox} style={{ background: slot.color }} />
        <span class={styles.runnerName}>New runner soon</span>
      </div>
    )
  }
  return (
    <button
      type="button"
      class={`${styles.runnerCard} ${equipped ? styles.equipped : ''}`}
      aria-pressed={equipped}
      aria-label={runnerName(slot)}
      onClick={() => onPick(slot.charId)}
    >
      <span class={styles.runnerHead} style={{ '--runner': slot.color }}>
        {head ? <img src={assetUrl(head)} alt="" draggable={false} /> : null}
      </span>
      <span class={styles.runnerName}>{runnerName(slot)}</span>
    </button>
  )
}

/** The Rides tab's data: the catalogue's rides, the equipped one, and equipping. */
export interface RidePick {
  rides: readonly CatalogRide[]
  equipped: RideId
  owns: (id: RideId) => boolean
  onPick: (id: RideId) => void
}

/**
 * Customise panel (PHASE3 T3.1): Runners / Rides tabs, the six costume slot icons and the runner
 * grid built from the art data. Slots are "Coming soon" until T3.8; the Rides tab equips a ride
 * (T3.11).
 */
export function CustomisePanel({
  runners,
  charId,
  onPick,
  rides,
}: {
  runners: readonly RunnerSlot[]
  charId: number
  onPick: (charId: number) => void
  rides: RidePick
}) {
  const [tab, setTab] = useState<Tab>('runners')
  const [slot, setSlot] = useState<CostumeSlot | null>(null)
  const tabs: readonly { id: Tab; label: string; icon: IconName }[] = [
    { id: 'runners', label: 'Runners', icon: 'runners' },
    { id: 'rides', label: 'Rides', icon: 'rides' },
  ]
  return (
    <section class={styles.panel} aria-label="Style">
      <div class={styles.bigTabs} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            class={`${styles.bigTab} ${tab === t.id ? styles.tabOn : ''}`}
            onClick={() => {
              setTab(t.id)
              setSlot(null)
            }}
          >
            <Icon name={t.icon} />
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'runners' && (
        <div class={styles.slotRow} role="group" aria-label="Costume slots">
          {SLOTS.map((s) => (
            <button
              key={s.id}
              type="button"
              class={`${styles.slotIcon} ${slot === s.id ? styles.tabOn : ''}`}
              aria-label={s.label}
              aria-pressed={slot === s.id}
              title={s.label}
              onClick={() => setSlot(slot === s.id ? null : s.id)}
            >
              <Icon name={s.id as IconName} />
            </button>
          ))}
        </div>
      )}
      <div class={styles.panelBody}>
        {tab === 'rides' ? (
          <RideGrid {...rides} />
        ) : slot ? (
          <ComingSoonGrid
            what={`${SLOTS.find((s) => s.id === slot)!.label} arrive in a later update.`}
          />
        ) : (
          <div class={styles.runnerGrid}>
            {runners.map((r) => (
              <RunnerCard key={r.kind} slot={r} equipped={r.charId === charId} onPick={onPick} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
